import { margins } from '@olist/styled-system';
import styled, { css } from 'styled-components';
import Caption from '../Text/Caption/Caption.js';
import '../Text/DetailedSubheading/DetailedSubheading.js';
import '../Text/OverlineLg/OverlineLg.js';
import '../Text/OverlineMd/OverlineMd.js';
import '../Text/ParagraphLg/ParagraphLg.js';
import '../Text/ParagraphMd/ParagraphMd.js';
import '../Text/SectionHeading/SectionHeading.js';
import '../Text/SectionSubheading/SectionSubheading.js';
import '../Text/Title/Title.js';

var RadioStylesState;
(function (RadioStylesState) {
    RadioStylesState["Error"] = "error";
    RadioStylesState["ErrorSelected"] = "errorSelected";
    RadioStylesState["ErrorHover"] = "errorHover";
    RadioStylesState["Hover"] = "hover";
    RadioStylesState["Focus"] = "focus";
    RadioStylesState["Active"] = "active";
    RadioStylesState["Disabled"] = "disabled";
    RadioStylesState["Selected"] = "selected";
    RadioStylesState["SelectedDisabled"] = "selectedDisabled";
})(RadioStylesState || (RadioStylesState = {}));
const GroupWrapper = styled.div `
  ${margins}
`;
const Wrapper = styled.div(({ hasLabel }) => css `
    display: inline-flex;
    align-items: center;
    height: 44px;
    position: relative;

    ${!hasLabel &&
    css `
      width: 44px;
      justify-content: center;
    `}
  `);
const Group = styled.div(({ inline }) => css `
    display: flex;
    flex-direction: column;

    ${inline &&
    css `
      flex-direction: row;
      ${Wrapper} + ${Wrapper} {
        margin-left: 32px;
      }
    `}
  `);
const radioStyles = ({ theme: { olist: { components }, }, state, }) => ({
    backgroundColor: components.radio['background-color'][state],
    boxShadow: components.radio['box-shadow'][state],
    borderColor: components.radio['border-color'][state],
});
const StyledRadio = styled.div(({ error, disabled, readOnly, theme }) => css `
    position: relative;
    width: ${theme.olist.components.radio.size};
    height: ${theme.olist.components.radio.size};
    pointer-events: none;
    box-sizing: border-box;

    border: ${theme.olist.components.radio['border-width']} solid
      ${theme.olist.components.radio['border-color'].rest};
    border-radius: ${theme.olist.components.radio.radius};
    background-color: ${theme.olist.components.radio['background-color'].rest};

    ${error &&
    !disabled &&
    !readOnly &&
    css `
      ${radioStyles({ theme, state: RadioStylesState.Error })}
    `}

    &:before {
      display: none;
      content: '';

      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);

      width: ${theme.olist.components.radio.dot.size};
      height: ${theme.olist.components.radio.dot.size};
      border-radius: ${theme.olist.components.radio.radius};

      background-color: ${theme.olist.components.radio.dot['background-color']};
    }
  `);
const Label = styled.label(({ theme }) => css `
    font-size: ${theme.olist.components.radio.label['font-size']};
    font-weight: ${theme.olist.components.radio.label['font-weight']};

    margin-left: ${theme.olist.components.radio.label['margin-left']};

    color: ${theme.olist.components.radio.label.color.rest};
    cursor: pointer;
  `);
const Field = styled.input(({ error, hasLabel, theme }) => css `
    margin: 0;
    padding: 0;

    position: absolute;
    top: 50%;
    left: 0;

    transform: translateY(-50%);

    width: ${hasLabel ? theme.olist.components.radio.size : '44px'};
    height: ${hasLabel ? theme.olist.components.radio.size : '44px'};

    opacity: 0;

    cursor: pointer;

    &:hover ~ ${StyledRadio} {
      ${radioStyles({ theme, state: RadioStylesState.Hover })}
    }

    ${error &&
    css `
      &:hover ~ ${StyledRadio} {
        ${radioStyles({ theme, state: RadioStylesState.ErrorHover })}
      }
    `}

    &:focus ~ ${StyledRadio} {
      ${radioStyles({ theme, state: RadioStylesState.Focus })}
    }

    &:active ~ ${StyledRadio} {
      ${radioStyles({ theme, state: RadioStylesState.Active })}

      &:before {
        display: block;
      }
    }

    &:checked ~ ${StyledRadio} {
      ${radioStyles({ theme, state: RadioStylesState.Selected })};

      &:before {
        display: block;
      }

      ${error &&
    css `
        ${radioStyles({ theme, state: RadioStylesState.ErrorSelected })};
      `}
    }

    &:checked:disabled ~ ${StyledRadio}, &:checked[readonly] ~ ${StyledRadio} {
      ${radioStyles({ theme, state: RadioStylesState.SelectedDisabled })};

      &:before {
        display: block;
      }
    }

    &:disabled,
    &:disabled:hover,
    &:disabled:active {
      cursor: not-allowed;

      & ~ ${StyledRadio} {
        ${radioStyles({ theme, state: RadioStylesState.Disabled })}

        &:before {
          display: none;
        }
      }

      & ~ ${Label} {
        color: ${theme.olist.components.radio.label.color.disabled};
        cursor: not-allowed;
      }
    }

    &[readonly]:disabled,
    &[readonly]:disabled:hover,
    &[readonly]:disabled:active {
      cursor: default;

      & ~ ${Label} {
        cursor: default;
      }
    }

    &[readonly] ~ ${Label}, &[readonly] ~ ${Label}:hover {
      color: ${theme.olist.components.radio.label.color.rest};
    }

    &:checked:disabled:hover ~ ${StyledRadio} {
      ${radioStyles({ theme, state: RadioStylesState.SelectedDisabled })};

      &:before {
        display: block;
      }
    }
  `);
const ErrorMessage = styled(Caption)(({ theme: { olist: { components: { radio }, }, }, }) => css `
    color: ${radio.errorMessage.color};
  `);

export { ErrorMessage, Field, Group, GroupWrapper, Label, StyledRadio, Wrapper };
